/**
 * usePackingMutation - Packing station mutations
 *
 * Provides mutation hooks for packing station operations:
 * - scanForPacking: Scan bin/garment QR to load order
 * - verifyItem: Verify an item during packing
 * - reportItemIssue: Report an issue with a P&P item
 * - completePacking: Complete packing for an order
 *
 * Follows useCatcherMutations.ts pattern with:
 * - Proper cache invalidation using tRPC utils
 * - Error handling delegated to components (modal feedback or inline state)
 */
import { trpc } from '@/lib/trpc';

/**
 * Mutation hook for scanning bin/garment QR to load order for packing.
 */
export function useScanForPacking() {
  const utils = trpc.useUtils();

  return trpc.packing.scanForPacking.useMutation({
    onSuccess: () => {
      utils.packing.getPackingStats.invalidate();
    },
    onError: () => {
      // Error handled by PackingPage modal feedback
    },
  });
}

/**
 * Mutation hook for verifying an item at packing station.
 * Uses optimistic updates for instant UI feedback (BLU-279).
 */
export function useVerifyItem() {
  const utils = trpc.useUtils();

  return trpc.packing.verifyItem.useMutation({
    onMutate: async (variables) => {
      // Cancel any in-flight getPackingOrder refetches to prevent overwriting optimistic update
      await utils.packing.getPackingOrder.cancel();

      // Snapshot current cache for rollback
      const previousData = utils.packing.getPackingOrder.getData({
        orderId: variables.orderId,
        locationId: variables.locationId,
      });

      // Optimistically update the item as verified
      utils.packing.getPackingOrder.setData(
        { orderId: variables.orderId, locationId: variables.locationId },
        (old) => {
          if (!old) return old;
          // Only increment verifiedCount if the item wasn't already verified (prevents double-count on re-scan)
          const alreadyVerified = old.items.some(
            (item) => item.garmentUnitId === variables.garmentUnitId && item.isVerified
          );
          return {
            ...old,
            items: old.items.map((item) =>
              item.garmentUnitId === variables.garmentUnitId
                ? { ...item, isVerified: true, verifiedAt: new Date().toISOString() }
                : item
            ),
            verifiedCount: alreadyVerified ? (old.verifiedCount ?? 0) : (old.verifiedCount ?? 0) + 1,
          };
        }
      );

      return { previousData, queryKey: { orderId: variables.orderId, locationId: variables.locationId } };
    },
    onError: (_error, _variables, context) => {
      // Rollback to snapshot on error
      if (context?.previousData) {
        utils.packing.getPackingOrder.setData(context.queryKey, context.previousData);
      }
    },
    onSuccess: (data, variables) => {
      // Merge authoritative verifiedAt from server response — no full refetch needed
      utils.packing.getPackingOrder.setData(
        { orderId: variables.orderId, locationId: variables.locationId },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.map((item) =>
              item.garmentUnitId === variables.garmentUnitId
                ? { ...item, verifiedAt: data.verifiedAt ?? item.verifiedAt }
                : item
            ),
          };
        }
      );
    },
    onSettled: (_data, _error, variables) => {
      // Reconcile cache with server truth after every verify (success or error).
      // Resolves potential cache drift from concurrent sessions or snapshot chain issues.
      utils.packing.getPackingOrder.invalidate({
        orderId: variables.orderId,
        locationId: variables.locationId,
      });
    },
  });
}

/**
 * Mutation hook for reporting an issue with a P&P item.
 * Note: onSuccess does NOT show toast - the component handles
 * the replacement dialog flow, which shows its own feedback.
 */
export function useReportIssue() {
  const utils = trpc.useUtils();

  return trpc.packing.reportItemIssue.useMutation({
    onSuccess: () => {
      // Let component handle replacement dialog
      utils.packing.getPackingOrder.invalidate();
    },
    onError: () => {
      // Error handled by PackingPage modal feedback
    },
  });
}

/**
 * Mutation hook for completing packing of an order.
 */
export function useCompletePacking() {
  const utils = trpc.useUtils();

  return trpc.packing.completePacking.useMutation({
    onSuccess: () => {
      utils.packing.getPackingStats.invalidate();
      utils.packing.getRecentlyPacked.invalidate();
      utils.bins.getBinOccupancy.invalidate();
    },
    onError: () => {
      // Error handled by ShippingPanel inline error state
    },
  });
}
